import PropTypes from 'prop-types';
import React from 'react';
import GS from '../grafsync/grafsync';
import Errored from './Errored';
import Loading from './Loading';
import cloneDeep from 'lodash/cloneDeep';

// The following function is from React and is a suggested appraoch to handle callbacks for unmounted components
// We can move this into a module if needed
const makeCancelable = (promise, reference) => {
    let hasCanceled_ = false;

    const wrappedPromise = new Promise((resolve, reject) => {
        promise.then(
            val => hasCanceled_ ? reject({ isCanceled: true, hasErrored: false, reference: reference }) : resolve(val),
            error => hasCanceled_ ? reject({ isCanceled: true, hasErrored: true, reference: reference }) : reject(error)
        );
    });

    return {
        promise: wrappedPromise,
        isCancelled() { return hasCanceled_; },
        cancel() {
            hasCanceled_ = true;
        },
    };
};

function withGS(WrappedComponent, objectEvents = ["ALL"], propsList = null, loadOptions = {}, entailment = false) {

    const gs = class extends React.Component {

        displayName() { return 'GSHOC'; }

        constructor(props) {
            super(props);
            this.state = {
                _isLoaded: null,
                _isLoading: true,
                _isErrored: null,
                _entailed: null,
                _version: null,
                _language: props.loadOptions && props.loadOptions.language ? props.loadOptions.language : [],
                pendingDeletion: null,
                pendingReplacement: null,
            };
            this.cancellable = null;
            if (!props.graph) {
                throw new Error("Missing graph property from load: " + props.id);
            }
        }

        // Just need to check if a load followed by a reload causes cancellable issues
        reloadData = () => {
            return new Promise(resolve => {
                this.getData({});
                resolve();
            });
        };

        /**
         * 
         * @param {*} options If supplied this overrided all over load option inputs. It is used by reload
         */
        loadData(options) {
            // Fixed load options may have been included in component construction
            // Dynamic values such as language may be supplied as a prop
            const mergedLoadOptions = options || Object.assign({}, loadOptions, this.props.loadOptions);
            this.cancellable = makeCancelable(GS.loadDataFromGraph([{
                graph: this.props.graph,
                iri: this.props.id
            }], mergedLoadOptions, entailment), this.props.id);
            return this.cancellable;
        }

        getData(options) {
            this.setState({ _isLoading: true }, () => {
                this.loadData(options)
                    .promise
                    .then(() => {
                        if (this.cancellable && this.cancellable.isCancelled()) return;
                        const { id, expectedTypes } = this.props;
                        const graph = this.props.graph;
                        const fullData = GS.data(id, graph, !this.props.handleNotAvailable);
                        if (fullData && expectedTypes && expectedTypes.length > 0 && fullData.type) {
                            const fullDataTypes = Array.isArray(fullData.type) ? fullData.type : [fullData.type];
                            if (fullDataTypes) {
                                const isValidType = expectedTypes.some(et => fullDataTypes.includes(et));
                                if (!isValidType) {
                                    throw new Error("Invalid resource type has been loaded.");
                                }
                            }
                        }
                        let dataToUse = fullData;
                        if (dataToUse) {
                            // GS.data returns a copied object filtered to only include propsList if supplied
                            if (propsList) {
                                dataToUse = Object.keys(GS.data(id, graph)).filter(key => propsList.includes(key))
                                    .reduce((obj, key) => {
                                        obj[key] = fullData[key];
                                        return obj;
                                    }, {});
                            }
                            this.setState(dataToUse);
                            this.setState({ _notAvailable: false });
                        }
                        else {
                            this.setState({ id, _notAvailable: true });
                        }
                        const langState = this.props.loadOptions && this.props.loadOptions.language ? this.props.loadOptions.language : [];
                        this.setState({ _isLoaded: true, _isLoading: false, _language: langState });
                        // For now we're binding same handle for all events
                        // We'll pass through any specific handlers at some point
                        // We need to be careful to only bind events once when resource is first loaded
                        if (dataToUse && objectEvents && !this.changeMonitor) {
                            this.changeMonitor = GS.bindChange(id, graph, this.handleGSevent);
                        }
                    })
                    .catch((e) => {
                        if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ _isErrored: e });
                    });
            });
        }

        componentDidMount() {
            // Note that loadData only actually loads from backend if not already loaded
            if (!this.props.id || this.props.id === "undefined" || this.props.id === undefined) throw new Error("An id property must be given to the GS HoC");
            this.getData();
        }

        static getDerivedStateFromProps(props, state) {
            if (props.id !== state.id && state._isLoaded !== null) throw new Error("Invalid reuse of GS HoC component in " + WrappedComponent.name + ", " + props.id + ", " + state.id);
            const propsLang = props.loadOptions && props.loadOptions.language ?
                Array.isArray(props.loadOptions.language) ? props.loadOptions.language : [props.loadOptions.language]
                :
                [];
            const stateLang = Array.isArray(state._language) ? state._language : [state._language];
            const sameLangs = (propsLang.length === stateLang.length) && propsLang.every((element, index) => element === stateLang[index]);
            if (!sameLangs) {
                return { _isLoaded: null, pendingDeletion: null, pendingReplacement: null, _version: null, _entailed: null };
            }
            return null;
        }

        componentDidUpdate() {
            if (!this.state._isLoading && this.state._isLoaded === null && !this.state._notAvailable) {
                // If we get here language options changed which might need a reload
                this.getData();
            }
        }

        componentWillUnmount() {
            if (this.cancellable) this.cancellable.cancel();
            const graph = this.props.graph;
            // Remove event binding for this component
            if (this.changeMonitor) GS.unbind(this.props.id, graph, this.changeMonitor);
        }

        // This method is deprecated for additions - use handleAdd. This is due to some ambiguities computing adds vs updates
        handleChange = (name, value, lang, datatype, index, propertyGraphIRI) => {
            const { id } = this.props;
            if (!name) throw new Error("A component must provide the property name");
            if (lang && datatype) throw new Error("It is not valid to supply both language and datatype for add operation");
            if (value !== 0 && (!value || value === "")) {
                // If value is empty essentially this is deleting the triple
                // This will not apply for lists or sets so need to think about that one
                GS.deleteProperty(id, this.props.graph, name, null, lang, datatype, propertyGraphIRI);
                return;
            }
            const doesItExist = GS.doesValueExist(id, this.props.graph, name, index, lang, value, datatype, propertyGraphIRI);
            if (!doesItExist) {
                // If property and value doesn't exist it must be an add action
                // Need to handle lang for appropriate properties
                // At this point we have no way of knowing datatype to be added if not defined in context file
                // So skos:notation for instance could vary between taxonomies
                // Either a SHACL shape will define it at some point or user input will define it
                // For taxonomies and especially skos:notation it will ultimately be user input
                GS.addProperty(id, this.props.graph, name, value, lang, datatype, propertyGraphIRI);
            }
            else {
                // Otherwise we're updating a value, possibly with a language
                // Note that we will need an index value if there is an array of language values, e.g. 'en: ['word1', 'words']
                GS.updateProperty(id, this.props.graph, name, value, lang, index);
            }
        };

        handleAdd = (name, value, lang = null, datatype = null) => {
            const { id } = this.props;
            if (!name || value === null || value === undefined) throw new Error("A component must provide the property name and value attributes");
            if (lang && datatype) throw new Error("It is not valid to supply both language and datatype for add operation");
            if (value === "") throw new Error("onAdd cannot be used with empty values");
            GS.addProperty(id, this.props.graph, name, value, lang, datatype);
        };

        handleUpdate = (name, value, lang, index, propertyGraphIRI) => {
            const { id } = this.props;
            if (!name) throw new Error("A component must provide the property name");
            if (value === null || value === undefined || value === "") throw new Error("An updated value must be supplied");
            // Note that we will need an index value if there is an array of language values, e.g. 'en: ['word1', 'words']
            GS.updateProperty(id, this.props.graph, name, value, lang, index, propertyGraphIRI);
        };

        handleDelete = (name, value, lang = null, datatype = null, propertyGraphIRI) => {
            const { id } = this.props;
            if (!name || !value) throw new Error("A component must provide the property name and value attributes");
            if (lang && datatype) throw new Error("It is not valid to supply both language and datatype for add operation");
            if (value === "") throw new Error("onDelete cannot be used with empty values");
            GS.deleteProperty(id, this.props.graph, name, value, lang, datatype, propertyGraphIRI);
        };

        handleGSevent = (eventType, eventData) => {
            // Only update state which the component is interested in to minise render triggers
            const graph = this.props.graph;
            if (eventType === "DELETERESOURCE") {
                const gsData = GS.data(this.props.id, graph);
                this.setState({ pendingDeletion: gsData.pendingDeletion });
                // Update the session version number of object
                this.setState({ _version: gsData._version });
                // Sometimes useful for a parent component to know the child has deleted
                if (this.props.deleteEvent) {
                    this.props.deleteEvent(this.props.id);
                }
            }
            else if (eventType === "REPLACERESOURCE") {
                const gsData = GS.data(this.props.id, graph);
                // Update the session version number of object
                this.setState({ pendingReplacement: true, _version: gsData._version });
            }
            else if (!propsList || (propsList && propsList.includes(eventData.property))) {
                const gsData = GS.data(this.props.id, graph);
                // We can't easily remove deleted properties from state so they will have the value 'undefined'
                this.setState({ [eventData.property]: gsData[eventData.property] });
                // Need to allow for entailment information in other places at some point
                // This includes properties that were entailed and are now not (i.e. caused by deletes)
                // So we update the state data for deleted entailment properties
                const existingEntailedProps = this.state._entailed ? this.state._entailed : {};
                const entailedProps = gsData._entailed ? Object.keys(gsData._entailed) : [];
                Object.keys(existingEntailedProps)
                    .filter(existingEntailedProp => !entailedProps.includes(existingEntailedProp))
                    .forEach(existingEntailedProp => {
                        this.setState({ [existingEntailedProp]: gsData[existingEntailedProp] });
                    });
                // Make sure the state entailment matches the GS entailment
                // Update the session version state of object
                this.setState({ _entailed: gsData._entailed, _version: gsData._version });
                // Sometimes useful for a parent component to know the child has changed
                if (this.props.updateEvent) {
                    this.props.updateEvent(this.props.id, eventData.property);
                }
            }
        };

        render() {
            // _isLoading gets passed down in case components need to avoid rendering until data is available
            // The wrapped component should check isLoading and render a holding view if true
            // By default this component will not render the wrapped component if loading
            // That won't always be desireable behaviour so we could allow for a prop to control that
            if (this.state._isErrored) return <Errored dataTest={"HOC-" + WrappedComponent.name} data={this.state._isErrored} />;
            // In some situations rendering loading would invalidate DOM structure so allow suppression
            if (this.state._isLoading && this.props.suppressLoading) return null;
            if (this.state._isLoading) return <Loading />;
            // Make sure to clone state to make immutable
            const propState = cloneDeep(this.state);

            return <WrappedComponent
                reload={this.reloadData}
                isLoading={this.state._isLoading}
                onDelete={this.handleDelete}
                onChange={this.handleChange}
                onAdd={this.handleAdd}
                onUpdate={this.handleUpdate}
                version={this.state._version}
                data={propState}
                {...this.props} />;
        }
    };

    gs.defaultProps = {
        handleNotAvailable: false,
        suppressLoading: false,
        expectedTypes: []
    };

    // Expected types is used to control invalid loading. We crosscheck against the loaded data.
    gs.propTypes = {
        objectEvents: PropTypes.array,
        graph: PropTypes.string,
        expectedTypes: PropTypes.array,
        suppressLoading: PropTypes.bool,
        id: PropTypes.string.isRequired,
        handleNotAvailable: PropTypes.bool,
        updateEvent: PropTypes.func,
        deleteEvent: PropTypes.func,
        entailment: PropTypes.bool,
        propsList: PropTypes.array,
        loadOptions: PropTypes.object
    };

    return gs;
}

export default withGS;